/**
 * UQ-1/UQ-2 — convert a receive-line quantity into canonical on-hand units.
 *
 * When the operator receives in shipping units (e.g. "1 bundle"), multiply
 * by `pieces_per_shipping_unit` (catalog) or an alias `unit_conversion_factor`
 * override. Receiving in canonical `unit` posts the qty as-entered.
 */

export type ReceiveQtyMode = "shipping" | "canonical";

export type ReceiveConversionSource =
  | { kind: "catalog" }
  | { kind: "alias"; vendorName: string };

export function resolveReceiveConversionFactor(opts: {
  piecesPerShippingUnit: number;
  /** Alias override (UQ-2); when set and > 0, wins over catalog. */
  aliasUnitConversionFactor?: number | null;
}): number {
  const alias = opts.aliasUnitConversionFactor;
  if (typeof alias === "number" && Number.isFinite(alias) && alias > 0) {
    return alias;
  }
  const catalog = opts.piecesPerShippingUnit;
  if (typeof catalog === "number" && Number.isFinite(catalog) && catalog > 0) {
    return catalog;
  }
  return 1;
}

/** Which factor is active for the receive UI source label. */
export function resolveReceiveConversionSource(opts: {
  aliasUnitConversionFactor?: number | null;
  aliasVendorName?: string | null;
}): ReceiveConversionSource {
  const alias = opts.aliasUnitConversionFactor;
  if (typeof alias === "number" && Number.isFinite(alias) && alias > 0) {
    const vendor = (opts.aliasVendorName ?? "").trim();
    return { kind: "alias", vendorName: vendor || "vendor" };
  }
  return { kind: "catalog" };
}

export function formatReceiveConversionSourceLabel(source: ReceiveConversionSource): string {
  if (source.kind === "alias") return `Vendor: ${source.vendorName}`;
  return "Catalog default";
}

export function convertReceiveQtyToCanonical(opts: {
  enteredQty: number;
  mode: ReceiveQtyMode;
  piecesPerShippingUnit: number;
  aliasUnitConversionFactor?: number | null;
}): number {
  const qty = opts.enteredQty;
  if (!Number.isFinite(qty) || qty <= 0) return 0;
  if (opts.mode === "canonical") return qty;
  const factor = resolveReceiveConversionFactor({
    piecesPerShippingUnit: opts.piecesPerShippingUnit,
    aliasUnitConversionFactor: opts.aliasUnitConversionFactor,
  });
  return qty * factor;
}

/** Per-piece weight (lbs) when shipping weight + conversion factor are set. */
export function perPieceWeightLbs(
  shippingUnitWeight: number,
  piecesPerShippingUnit: number
): number | null {
  if (!(shippingUnitWeight > 0) || !(piecesPerShippingUnit > 0)) return null;
  return shippingUnitWeight / piecesPerShippingUnit;
}

/** Whether the receive UI should offer a shipping-vs-canonical qty toggle. */
export function shouldOfferReceiveUnitToggle(material: {
  unit: string;
  shipping_unit: string;
}): boolean {
  const unit = (material.unit || "").trim().toLowerCase();
  const ship = (material.shipping_unit || "").trim().toLowerCase();
  return Boolean(ship) && ship !== unit;
}

/** Default receive mode when a material is selected. */
export function defaultReceiveQtyMode(material: {
  unit: string;
  shipping_unit: string;
  pieces_per_shipping_unit: number;
}): ReceiveQtyMode {
  if (!shouldOfferReceiveUnitToggle(material)) return "canonical";
  if (Number(material.pieces_per_shipping_unit) > 1) return "shipping";
  return "shipping";
}

/**
 * OCR receipt lines: invoice qty is treated as shipping units when the
 * material ships differently from its canonical unit, or when an alias
 * conversion factor other than 1 is in play.
 */
export function convertOcrReceiptQtyToCanonical(opts: {
  enteredQty: number;
  material: { unit: string; shipping_unit: string; pieces_per_shipping_unit: number } | null | undefined;
  aliasUnitConversionFactor?: number | null;
}): number {
  const mat = opts.material;
  const aliasFactor = opts.aliasUnitConversionFactor;
  const mode: ReceiveQtyMode =
    mat &&
    (shouldOfferReceiveUnitToggle(mat) ||
      (typeof aliasFactor === "number" && aliasFactor > 0 && aliasFactor !== 1))
      ? "shipping"
      : "canonical";
  return convertReceiveQtyToCanonical({
    enteredQty: opts.enteredQty,
    mode,
    piecesPerShippingUnit: Number(mat?.pieces_per_shipping_unit) || 1,
    aliasUnitConversionFactor: aliasFactor,
  });
}
